import React, { useMemo } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { Permission, type PermissionValue } from '../../auth/types';
import { FeedPost } from '../../feed/types';
import { Icon } from '../../../components/common/Icon';

/**
 * Maps user permissions to department names used in feed mentions
 */
function getDepartmentsFromPermissions(permissions: PermissionValue[]): string[] {
    const departments: string[] = [];

    if (permissions.includes(Permission.SELF_CHECK_IN)) {
        departments.push('Cast');
    }
    if (permissions.includes(Permission.EDIT_PROPS)) {
        departments.push('Props');
    }
    if (permissions.includes(Permission.EDIT_SOUND)) {
        departments.push('Sound');
    }
    if (permissions.includes(Permission.EDIT_RUNSHEET)) {
        departments.push('Stage Management');
    }
    if (permissions.includes(Permission.EDIT_SCHEDULER)) {
        departments.push('Director');
    }
    if (permissions.includes(Permission.EDIT_BLOCKING)) {
        departments.push('Choreography', 'Scenery');
    }

    // Always include Announcement - everyone should see announcements
    departments.push('Announcement');

    return departments;
}

export const AnnouncementsWidget: React.FC = () => {
    const feedPosts = useAppStore(state => state.feedPosts || []);
    const user = useAppStore(state => state.auth.user);
    const cloudMember = useAppStore(state => state.cloud.member);
    const roles = useAppStore(state => state.admin.roles);

    // Get user permissions
    const userRole = cloudMember ? roles.find(r => r.id === cloudMember.roleId) : null;
    const permissions = useMemo(
        () => user?.isSuperAdmin
            ? Object.values(Permission)
            : (userRole?.permissions || []),
        [user?.isSuperAdmin, userRole?.permissions]
    );

    // Get user's departments
    const userDepartments = useMemo(() => 
        getDepartmentsFromPermissions(permissions), [permissions]);

    // Get relevant posts: pinned OR mentioning user's department
    const relevantPosts = useMemo(() => {
        return feedPosts
            .filter((post: FeedPost) => {
                if (post.deleted) return false;
                
                // Include if pinned
                if (post.pinned) return true;
                
                // Include if mentions any of user's departments
                const mentionsDepartment = post.mentions?.some(mention => 
                    mention.type === 'department' && 
                    userDepartments.includes(mention.id)
                );
                
                return mentionsDepartment;
            })
            .toSorted((a: FeedPost, b: FeedPost) => {
                // Pinned first, then by date
                if (a.pinned && !b.pinned) return -1;
                if (!a.pinned && b.pinned) return 1;
                return b.createdAt - a.createdAt;
            })
            .slice(0, 5); // Limit to 5 most recent
    }, [feedPosts, userDepartments]);

    if (relevantPosts.length === 0) {
        return (
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Icon name="campaign" className="text-primary" />
                    Announcements
                </h3>
                <div className="text-center py-8">
                    <Icon name="notifications_none" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No announcements for your department</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Icon name="campaign" className="text-primary" />
                Announcements
            </h3>
            <div className="space-y-3">
                {relevantPosts.map((post: FeedPost) => (
                    <div 
                        key={post.id}
                        className={`p-3 rounded-lg border ${
                            post.pinned 
                                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        }`}
                    >
                        <div className="flex items-start gap-2">
                            <Icon name={post.pinned ? 'push_pin' : 'alternate_email'} size="sm" className={`mt-0.5 ${
                                post.pinned 
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-blue-600 dark:text-blue-400'
                            }`} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {post.authorName}
                                    </p>
                                    {!post.pinned && post.mentions?.length > 0 && (
                                        <span className="text-xs text-blue-600 dark:text-blue-400">
                                            @{post.mentions.filter(m => m.type === 'department').map(m => m.id).join(', @')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 line-clamp-2">
                                    {post.content.replace(/<[^>]*>/g, '')}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {new Date(post.createdAt).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
